import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import { useSegment } from 'common/hooks/segment';
import { Input } from 'components/SignInput';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createCredential,
  CredentialUpdateCode,
  TerminalCredential,
  updateCredential,
} from 'services/clients/credentials';
import { QueryKeys } from '../../queries';

interface Props {
  credential: TerminalCredential;
  onClose: () => void;
}

interface FormValues {
  login: string;
  password: string;
}

export const UpdateForm: React.FC<Props> = (props) => {
  const { credential, onClose } = props;

  const updateMutation = useMutation(updateCredential);
  const createMutation = useMutation(createCredential);

  const queryClient = useQueryClient();
  const { track } = useSegment();

  const { register, handleSubmit, formState, setError } = useForm<FormValues>({
    defaultValues: {
      login: credential?.status !== 'missing' ? credential.login : '' || '',
      password: '',
    },
    mode: 'all',
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!credential) {
      return;
    }

    try {
      if (credential.status === 'missing') {
        await createMutation.mutateAsync(
          { portalName: credential.portal.name, ...values },
          {
            onSuccess: () => {
              track(
                'User added credential',
                {
                  portal: credential.portal.name,
                  method: 'row-add-button',
                  success: true,
                  error: '',
                },
                { includeCompany: true, includeUser: true }
              );
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
              track(
                'User added credential',
                {
                  portal: credential.portal.name,
                  method: 'row-add-button',
                  success: false,
                  error: error.message,
                },
                { includeCompany: true, includeUser: true }
              );
            },
          }
        );
      } else {
        await updateMutation.mutateAsync(
          { credentialId: credential.id, ...values },
          {
            onSuccess: () => {
              track(
                'User updated credential',
                {
                  portal: credential.portal.name,
                  success: true,
                  error: '',
                },
                { includeCompany: true, includeUser: true }
              );
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
              track(
                'User updated credential',
                {
                  portal: credential.portal.name,
                  success: false,
                  error: error.message,
                },
                { includeCompany: true, includeUser: true }
              );
            },
          }
        );
      }
      queryClient.invalidateQueries([QueryKeys.TerminalCredentials]);
      queryClient.invalidateQueries([QueryKeys.MissingCredentialsCount]);
      onClose();
    } catch (e) {
      let message = 'An error occurred';
      if (axios.isAxiosError(e)) {
        const code: CredentialUpdateCode | undefined = e.response?.data.code;
        if (code) {
          switch (code) {
            case 'credential_already_exists':
              message = 'This email or username is already in use';
              break;
            case 'authentication_failed':
            case 'invalid_credential':
              message = 'Login error, username or email and password don’t match';
              break;
            default:
              break;
          }
        }
      }
      // eslint-disable-next-line no-console
      console.error(e);
      setError('login', { type: 'custom', message }, { shouldFocus: true });
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-8" autoComplete="off">
      <div className="space-y-4">
        <Input
          label="Email or username*"
          type="text"
          inputProps={register('login', { required: true })}
          error={formState.errors.login}
          autoFocus
          placeholder="Terminal login"
        />
        <Input
          label="Password*"
          type="password"
          inputProps={register('password', { required: true })}
          error={formState.errors.password}
          placeholder="Enter your password"
        />
      </div>
      <div className="flex justify-end">
        <LoadingButton
          variant="contained"
          type="submit"
          loading={updateMutation.isLoading || createMutation.isLoading}
          disabled={!formState.isValid}
          size="large"
          className="w-[164px]"
        >
          <h3>Continue</h3>
        </LoadingButton>
      </div>
    </form>
  );
};
UpdateForm.displayName = 'UpdateForm';
